import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import useOrderQuery from 'api/queries/useOrderQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import OrderItemModal from 'features/account/orders/OrderItemModal';
import OrderShipment from 'features/account/orders/OrderShipment';
import OrderSummary from 'features/account/orders/OrderSummary';
import { Breadcrumbs } from 'honeydew/Breadcrumbs';
import Card from 'honeydew/Card';
import Loader from 'honeydew/Loader';
import Tag from 'honeydew/Tag';
import { formatDateString } from 'utils/dateUtils';
import { closeModal, openModal } from 'utils/globalUtils';
import { splitFreeFromPaidItems } from 'utils/orderUtils';

const PAGE_BREADCRUMBS = [{ id: 1, title: 'My Orders', url: '/account/orders' }];

// map drinks shipment data to new object to match non-wine shipment data structure
const generateDrinksOrderObject = (order) => {
    const drinksOrder = order.drinksShipments[0] || {};
    drinksOrder.hasDrinks = true;
    drinksOrder.itemCount = order.drinksBundles.concat(order.drinksItems).length ?? 0;
    drinksOrder.items = order.drinksBundles.concat(order.drinksItems) ?? [];
    drinksOrder.customerDrinksOrderFeedback = order.customerDrinksOrderFeedback;
    drinksOrder.shipmentID = drinksOrder.drinksOrderID;
    drinksOrder.createdAt = {
        date: order.createdOn,
    };

    return drinksOrder;
};

function OrderDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: account = {} } = useAccountQuery();
    const { data: subscription = {} } = useSubscriptionQuery();
    const { data: orderDetailsData = {}, isLoading: isOrderLoading } = useOrderQuery({ orderID: id });

    const { featureCanAddDriverTips } = account.featureFlags ?? {};
    const { firstName, lastName } = subscription?.shippingAddr ?? {};
    const { expMonth, expYear, last4 } = subscription?.billing ?? {};

    const order = orderDetailsData?.orderInfo ?? {};

    const orderShippingAddressArray = order?.shipAddr?.split('  ');

    const [orderShipments, setOrderShipments] = useState([]);
    const [multiShip, setMultiShip] = useState(false);
    const [cancelledOrder, setCancelledOrder] = useState(false);
    const [productModal, setProductModal] = useState(false);
    const [activeModalItem, setActiveModalItem] = useState({});

    const isImperfectLegacy = order?.source === 'imperfect';

    const openProductModal = (item) => {
        openModal(setProductModal);
        setActiveModalItem(item);
    };

    const closeProductModal = () => {
        closeModal(setProductModal);
        setActiveModalItem({});
    };

    const getTagStatus = () => {
        if (cancelledOrder) {
            return 'You will not be charged';
        }

        return `Charged ${formatDateString(order.createdOn)}`;
    };

    useEffect(() => {
        if (order.shipments || order.drinksShipments) {
            setMultiShip(order.shipments.length + order.drinksShipments.length > 1);
            setCancelledOrder(
                (order.shipments.length > 0 && order.shipments[0].shippingStatus === 'CANCELLED') ||
                    (order.drinksShipments.length > 0 && order.drinksShipments[0].shippingStatus === 'CANCELLED')
            );

            // Split free items (perks/GWP) from paid items with the same SKU
            const shipments = order.shipments.map((shipment) => ({
                ...shipment,
                items: splitFreeFromPaidItems(shipment.items || []),
            }));

            if (order.hasDrinks) {
                const drinksOrder = generateDrinksOrderObject(order);
                shipments.push(drinksOrder);
            }

            setOrderShipments(shipments);
        }
    }, [order]);

    useEffect(() => {
        if (!isOrderLoading && !order?.shipments) {
            navigate('/account/orders');
        }
    }, [isOrderLoading]);

    if ((!order.shopOrderNum && !subscription.id) || isOrderLoading || !order?.shipments) {
        return (
            <div className="flex h-[300px] items-center justify-center">
                <Loader width="60px" />
            </div>
        );
    }

    return (
        <section>
            <Breadcrumbs breadcrumbs={PAGE_BREADCRUMBS} />

            <Card marginClasses="-mx-20 mt-20 rounded-none! md:mx-0 md:rounded-lg!">
                <div className="text-body-sm -mx-20 -mt-30 block rounded-tl-lg rounded-tr-lg bg-off-white px-20 py-5 lg:-mx-30 lg:-mt-30 lg:px-30 if:bg-oat">
                    <p>
                        Order #{order.shopOrderNum}
                        {multiShip && (
                            <span className="text-body-sm">
                                {' '}
                                | <span className="font-grotesk-bold">This order shipped in multiple boxes.</span>
                            </span>
                        )}
                    </p>
                </div>
                <div>
                    {multiShip && (
                        <h3 className="text-body-lg mt-30 font-grotesk-bold">
                            {formatDateString(order.shipments[0].deliverDate)} Order
                        </h3>
                    )}
                    {orderShipments.map((shipment, i) => (
                        <OrderShipment
                            key={shipment.shipmentID}
                            shipment={shipment}
                            order={order}
                            boxNumber={i + 1}
                            openProductModal={openProductModal}
                            cancelledOrder={cancelledOrder}
                            multiShip={multiShip}
                            isImperfectLegacy={isImperfectLegacy}
                        />
                    ))}
                </div>

                <OrderSummary
                    order={order}
                    isImperfectLegacy={isImperfectLegacy}
                    featureCanAddDriverTips={featureCanAddDriverTips}
                />
            </Card>
            <Card marginClasses="mt-20 text-body-sm -mx-20 md:mx-0 rounded-none! md:rounded-lg!">
                <div className="grid-cols-2 justify-between md:grid">
                    <div className="mb-20">
                        <p className="mb-5 font-grotesk-bold">Shipping Address</p>
                        <div>
                            <p>
                                {firstName} {lastName}
                            </p>
                            <p>{orderShippingAddressArray?.[0]}</p>
                            <p>{orderShippingAddressArray?.[1]}</p>
                        </div>
                    </div>

                    <div>
                        <p className="mb-5 font-grotesk-bold">Payment Method</p>
                        <div className="gap-30 md:flex">
                            <div>
                                <p>Credit Card</p>
                                {/* NOTE: this is the last4 of your card on file, not necessarily the last4 of the card used for the order */}
                                <p>Card: **** **** **** {last4}</p>
                                <p className="mb-10">
                                    Expires on {expMonth}/{expYear}
                                </p>
                                {!isImperfectLegacy && <Tag variant="dark">{getTagStatus().toUpperCase()}</Tag>}
                            </div>
                            {order?.charge?.ebtLast4 && (
                                <div className="mt-15 md:mt-0" data-testid="ebt-charge-tag">
                                    <p>EBT</p>
                                    <p className="mb-10">Card: **** **** **** {order?.charge?.ebtLast4}</p>
                                    {order?.charge?.ebtChargeDate && (
                                        <Tag variant="dark">
                                            CHARGED
                                            {'\u00A0'}
                                            <span className="uppercase">
                                                {formatDateString(order.charge.ebtChargeDate).toUpperCase()}
                                            </span>
                                        </Tag>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            {productModal && (
                <OrderItemModal
                    item={activeModalItem}
                    closeProductModal={closeProductModal}
                    productModal={productModal}
                />
            )}
        </section>
    );
}

export { OrderDetails as Component };
